import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang, LangProvider } from '../../hooks/useLang';
import AuthModal from './components/AuthModal';
import PostDetail from './components/PostDetail';
import CreatePostModal from './components/CreatePostModal';
import AdminPanel from './components/AdminPanel';
import BBSSEOHead from './components/BBSSEOHead';
import type { BBSUser, BBSPost } from './types';
import { CATEGORIES, MOCK_POSTS, MOCK_USERS } from './types';

// BBS-specific multilang labels
const BBS_LABELS: Record<string, Record<string, string>> = {
  backToMain: { zh: '← 返回主站', en: '← Back to Main', fr: '← Retour au Site', es: '← Volver al Sitio', pt: '← Voltar ao Site', ja: '← メインサイトへ', de: '← Zurück zur Hauptseite', ko: '← 메인으로', ar: '← العودة للرئيسية', ru: '← На Главную' },
  adminPanel: { zh: '管理员面板', en: 'Admin Panel', fr: 'Panneau Admin', es: 'Panel Admin', pt: 'Painel Admin', ja: '管理パネル', de: 'Admin-Panel', ko: '관리자 패널', ar: 'لوحة الإدارة', ru: 'Панель Администратора' },
  admin: { zh: '管理员', en: 'Admin', fr: 'Admin', es: 'Admin', pt: 'Admin', ja: '管理者', de: 'Admin', ko: '관리자', ar: 'مدير', ru: 'Администратор' },
  logout: { zh: '退出', en: 'Logout', fr: 'Déconnexion', es: 'Cerrar Sesión', pt: 'Sair', ja: 'ログアウト', de: 'Abmelden', ko: '로그아웃', ar: 'تسجيل الخروج', ru: 'Выйти' },
  loginRegister: { zh: '登录 / 注册', en: 'Login / Register', fr: 'Connexion / Inscription', es: 'Iniciar Sesión / Registrarse', pt: 'Entrar / Registrar', ja: 'ログイン / 登録', de: 'Anmelden / Registrieren', ko: '로그인 / 회원가입', ar: 'تسجيل الدخول / التسجيل', ru: 'Войти / Зарегистрироваться' },
  forumTitle: { zh: 'RWA99 社区论坛', en: 'RWA99 Community Forum', fr: 'Forum Communautaire RWA99', es: 'Foro Comunitario RWA99', pt: 'Fórum da Comunidade RWA99', ja: 'RWA99 コミュニティフォーラム', de: 'RWA99 Community-Forum', ko: 'RWA99 커뮤니티 포럼', ar: 'منتدى مجتمع RWA99', ru: 'Форум Сообщества RWA99' },
  forumSubtitle: { zh: '区块链 · RWA · 政策解读 · 技术分享 · 资料中心 · 合规交流', en: 'Blockchain · RWA · Policy · Tech · Resources · Compliance', fr: 'Blockchain · RWA · Politique · Tech · Ressources · Conformité', es: 'Blockchain · RWA · Política · Tecnología · Recursos · Cumplimiento', pt: 'Blockchain · RWA · Política · Tecnologia · Recursos · Conformidade', ja: 'ブロックチェーン · RWA · 政策 · 技術 · リソース · コンプライアンス', de: 'Blockchain · RWA · Politik · Technik · Ressourcen · Compliance', ko: '블록체인 · RWA · 정책 · 기술 · 자료 · 컴플라이언스', ar: 'بلوكتشين · RWA · سياسة · تقنية · موارد · امتثال', ru: 'Блокчейн · RWA · Политика · Технологии · Ресурсы · Соответствие' },
  posts: { zh: '帖子', en: 'Posts', fr: 'Posts', es: 'Publicaciones', pt: 'Posts', ja: '投稿', de: 'Beiträge', ko: '게시물', ar: 'منشورات', ru: 'Постов' },
  members: { zh: '成员', en: 'Members', fr: 'Membres', es: 'Miembros', pt: 'Membros', ja: 'メンバー', de: 'Mitglieder', ko: '회원', ar: 'أعضاء', ru: 'Участников' },
  activeToday: { zh: '今日活跃', en: 'Active Today', fr: "Actifs Aujourd'hui", es: 'Activos Hoy', pt: 'Ativos Hoje', ja: '今日のアクティブ', de: 'Heute Aktiv', ko: '오늘 활성', ar: 'نشط اليوم', ru: 'Активных Сегодня' },
  newPost: { zh: '发布新帖', en: 'New Post', fr: 'Nouveau Post', es: 'Nueva Publicación', pt: 'Nova Publicação', ja: '新規投稿', de: 'Neuer Beitrag', ko: '새 게시물', ar: 'منشور جديد', ru: 'Новый Пост' },
  loginToPost: { zh: '登录后发帖', en: 'Login to Post', fr: 'Connectez-vous pour Poster', es: 'Inicia Sesión para Publicar', pt: 'Entre para Publicar', ja: 'ログインして投稿', de: 'Anmelden zum Posten', ko: '로그인 후 게시', ar: 'سجل الدخول للنشر', ru: 'Войдите для Публикации' },
  categories: { zh: '版块分类', en: 'Categories', fr: 'Catégories', es: 'Categorías', pt: 'Categorias', ja: 'カテゴリ', de: 'Kategorien', ko: '카테고리', ar: 'الفئات', ru: 'Категории' },
  contactAdmin: { zh: '联系站长', en: 'Contact Admin', fr: "Contacter l'Admin", es: 'Contactar Admin', pt: 'Contatar Admin', ja: '管理者に連絡', de: 'Admin Kontaktieren', ko: '관리자 연락', ar: 'تواصل مع المدير', ru: 'Связаться с Администратором' },
  complianceNotice: { zh: '合规提示', en: 'Compliance Notice', fr: 'Avis de Conformité', es: 'Aviso de Cumplimiento', pt: 'Aviso de Conformidade', ja: 'コンプライアンス通知', de: 'Compliance-Hinweis', ko: '컴플라이언스 공지', ar: 'إشعار الامتثال', ru: 'Уведомление о Соответствии' },
  complianceText: { zh: '本论坛仅供合规知识交流，不提供投资建议，不允许募资推介。所有内容须遵守中国内地及香港现行法律法规。', en: 'This forum is for compliance knowledge exchange only. No investment advice or fundraising is permitted. All content must comply with applicable laws in mainland China and Hong Kong.', fr: 'Ce forum est uniquement destiné à l\'échange de connaissances en matière de conformité. Aucun conseil en investissement ou collecte de fonds n\'est autorisé.', es: 'Este foro es solo para intercambio de conocimientos de cumplimiento. No se permite asesoramiento de inversión ni recaudación de fondos.', pt: 'Este fórum é apenas para troca de conhecimentos de conformidade. Nenhum conselho de investimento ou captação de recursos é permitido.', ja: 'このフォーラムはコンプライアンス知識の交換のみを目的としています。投資アドバイスや資金調達は許可されていません。', de: 'Dieses Forum dient nur dem Compliance-Wissensaustausch. Anlageberatung oder Fundraising ist nicht gestattet.', ko: '이 포럼은 컴플라이언스 지식 교환만을 위한 것입니다. 투자 조언이나 자금 모집은 허용되지 않습니다.', ar: 'هذا المنتدى لتبادل المعرفة بالامتثال فقط. لا يُسمح بتقديم نصائح استثمارية أو جمع التبرعات.', ru: 'Этот форум предназначен только для обмена знаниями о соответствии. Инвестиционные советы и сбор средств запрещены.' },
  latest: { zh: '最新', en: 'Latest', fr: 'Récent', es: 'Reciente', pt: 'Recente', ja: '最新', de: 'Neueste', ko: '최신', ar: 'الأحدث', ru: 'Новые' },
  hot: { zh: '最热', en: 'Hot', fr: 'Populaire', es: 'Popular', pt: 'Popular', ja: '人気', de: 'Beliebt', ko: '인기', ar: 'الأكثر شعبية', ru: 'Популярные' },
  noPosts: { zh: '该版块暂无帖子', en: 'No posts in this category', fr: 'Aucun post dans cette catégorie', es: 'No hay publicaciones en esta categoría', pt: 'Nenhuma publicação nesta categoria', ja: 'このカテゴリに投稿はありません', de: 'Keine Beiträge in dieser Kategorie', ko: '이 카테고리에 게시물이 없습니다', ar: 'لا توجد منشورات في هذه الفئة', ru: 'Нет постов в этой категории' },
  beFirst: { zh: '来发第一帖', en: 'Be the First to Post', fr: 'Soyez le Premier à Poster', es: 'Sé el Primero en Publicar', pt: 'Seja o Primeiro a Publicar', ja: '最初の投稿をする', de: 'Erster Beitrag Sein', ko: '첫 번째 게시물 작성', ar: 'كن أول من ينشر', ru: 'Будьте Первым' },
  pinned: { zh: '置顶', en: 'Pinned', fr: 'Épinglé', es: 'Fijado', pt: 'Fixado', ja: '固定', de: 'Angeheftet', ko: '고정', ar: 'مثبت', ru: 'Закреплено' },
  attachments: { zh: '个附件', en: 'Attachments', fr: 'Pièces Jointes', es: 'Adjuntos', pt: 'Anexos', ja: '添付ファイル', de: 'Anhänge', ko: '첨부파일', ar: 'مرفقات', ru: 'Вложений' },
  items: { zh: '条', en: 'items', fr: 'éléments', es: 'elementos', pt: 'itens', ja: '件', de: 'Einträge', ko: '개', ar: 'عناصر', ru: 'записей' },
};

function getBL(key: string, lang: string): string {
  return BBS_LABELS[key]?.[lang] ?? BBS_LABELS[key]?.['en'] ?? key;
}

// Category labels multilang
const CAT_LABELS: Record<string, Record<string, string>> = {
  'all': { zh: '全部帖子', en: 'All Posts', fr: 'Tous les Posts', es: 'Todos los Posts', pt: 'Todos os Posts', ja: 'すべての投稿', de: 'Alle Beiträge', ko: '모든 게시물', ar: 'جميع المنشورات', ru: 'Все Посты' },
  'rwa-discuss': { zh: 'RWA讨论', en: 'RWA Discussion', fr: 'Discussion RWA', es: 'Discusión RWA', pt: 'Discussão RWA', ja: 'RWA議論', de: 'RWA-Diskussion', ko: 'RWA 토론', ar: 'نقاش RWA', ru: 'Обсуждение RWA' },
  'policy': { zh: '政策解读', en: 'Policy Analysis', fr: 'Analyse Politique', es: 'Análisis de Política', pt: 'Análise de Política', ja: '政策分析', de: 'Politikanalyse', ko: '정책 분석', ar: 'تحليل السياسات', ru: 'Анализ Политики' },
  'tech': { zh: '技术分享', en: 'Tech Sharing', fr: 'Partage Tech', es: 'Compartir Tecnología', pt: 'Compartilhamento Tech', ja: '技術共有', de: 'Tech-Sharing', ko: '기술 공유', ar: 'مشاركة التقنية', ru: 'Технологии' },
  'case': { zh: '案例分析', en: 'Case Analysis', fr: 'Analyse de Cas', es: 'Análisis de Casos', pt: 'Análise de Casos', ja: 'ケース分析', de: 'Fallanalyse', ko: '사례 분석', ar: 'تحليل الحالات', ru: 'Анализ Случаев' },
  'market': { zh: '市场资讯', en: 'Market News', fr: 'Actualités Marché', es: 'Noticias del Mercado', pt: 'Notícias do Mercado', ja: '市場ニュース', de: 'Marktnachrichten', ko: '시장 뉴스', ar: 'أخبار السوق', ru: 'Новости Рынка' },
  'files': { zh: '文件中心', en: 'File Center', fr: 'Centre de Fichiers', es: 'Centro de Archivos', pt: 'Centro de Arquivos', ja: 'ファイルセンター', de: 'Dateizentrum', ko: '파일 센터', ar: 'مركز الملفات', ru: 'Центр Файлов' },
};

const FILE_ICONS: Record<string, string> = {
  pdf: 'ri-file-pdf-2-line',
  doc: 'ri-file-word-line',
  docx: 'ri-file-word-line',
  xlsx: 'ri-file-excel-line',
  xls: 'ri-file-excel-line',
  ppt: 'ri-file-ppt-line',
  pptx: 'ri-file-ppt-line',
};
const FILE_COLORS: Record<string, string> = {
  pdf: '#DC2626',
  doc: '#1D4ED8',
  docx: '#1D4ED8',
  xlsx: '#059669',
  xls: '#059669',
  ppt: '#EA580C',
  pptx: '#EA580C',
};

const getUser = (id: string) => MOCK_USERS.find((u) => u.id === id) || MOCK_USERS[0];

function BBSContent() {
  const { lang, t } = useLang();
  const [posts, setPosts] = useState<BBSPost[]>(MOCK_POSTS);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentUser, setCurrentUser] = useState<BBSUser | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BBSPost | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const filteredPosts = posts
    .filter((p) => activeCategory === 'all' || p.category === activeCategory)
    .sort((a, b) => {
      if (sortBy === 'hot') return b.likes + b.views - (a.likes + a.views);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const pinnedPosts = filteredPosts.filter((p) => p.pinned);
  const normalPosts = filteredPosts.filter((p) => !p.pinned);

  const handleLogin = (user: { id: string; username: string; email: string; avatar: string; role: 'admin' | 'member' }) => {
    const fullUser: BBSUser = {
      ...user,
      bio: '',
      joinDate: new Date().toISOString().slice(0, 10),
      postCount: 0,
    };
    setCurrentUser(fullUser);
  };

  const handleCreatePost = (postData: Omit<BBSPost, 'id' | 'likes' | 'views' | 'replies' | 'pinned' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const newPost: BBSPost = {
      ...postData,
      id: `post_${Date.now()}`,
      likes: 0,
      views: 0,
      replies: [],
      pinned: false,
      createdAt: now,
      updatedAt: now,
    };
    setPosts((prev) => [newPost, ...prev]);
  };

  const handleReply = (postId: string, content: string) => {
    if (!currentUser) return;
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              replies: [
                ...p.replies,
                {
                  id: `r_${Date.now()}`,
                  content,
                  authorId: currentUser.id,
                  likes: 0,
                  createdAt: now,
                },
              ],
            }
          : p
      )
    );
    if (selectedPost?.id === postId) {
      setSelectedPost((prev) =>
        prev
          ? {
              ...prev,
              replies: [
                ...prev.replies,
                { id: `r_${Date.now()}`, content, authorId: currentUser.id, likes: 0, createdAt: now },
              ],
            }
          : prev
      );
    }
  };

  const catInfo = CATEGORIES.find((c) => c.id === activeCategory) || CATEGORIES[0];
  const catLabel = CAT_LABELS[activeCategory]?.[lang] ?? catInfo.label;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #F8F5FF 0%, #FFF8F0 100%)' }}>
      <BBSSEOHead />
      {/* Top Banner */}
      <div style={{ background: 'linear-gradient(135deg, #2E1065, #4C1D95)' }}>
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 cursor-pointer group">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center text-white font-black shadow-lg">R</div>
              <span className="text-white font-black text-base">RWA<span className="text-[#F59E0B]">99</span><span className="text-white/40 text-sm font-normal">.cn</span></span>
              <span className="text-white/40 text-xs hidden sm:block group-hover:text-white/60 transition-colors">{getBL('backToMain', lang)}</span>
            </Link>
            <div className="flex items-center gap-3">
              {currentUser?.role === 'admin' && (
                <button
                  onClick={() => setAdminOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F59E0B]/20 border border-[#F59E0B]/40 rounded-full text-[#F59E0B] text-xs font-bold cursor-pointer hover:bg-[#F59E0B]/30 transition-all whitespace-nowrap"
                >
                  <i className="ri-admin-line text-xs" />
                  {getBL('adminPanel', lang)}
                </button>
              )}
              {currentUser ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                    <div className="w-6 h-6 rounded-full bg-[#7C3AED] flex items-center justify-center text-white text-xs font-black">{currentUser.avatar}</div>
                    <span className="text-white text-xs font-semibold">{currentUser.username}</span>
                    {currentUser.role === 'admin' && <span className="text-[#F59E0B] text-[10px]">{getBL('admin', lang)}</span>}
                  </div>
                  <button
                    onClick={() => setCurrentUser(null)}
                    className="text-white/40 text-xs hover:text-white transition-colors cursor-pointer"
                  >
                    {getBL('logout', lang)}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAuthOpen(true)}
                  className="px-4 py-1.5 bg-[#7C3AED] text-white text-xs font-bold rounded-full hover:bg-[#6D28D9] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {getBL('loginRegister', lang)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #4C1D95, #7C3AED)' }} className="pb-8 pt-6">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-discuss-line text-[#F59E0B] text-xl" />
                <h1 className="text-white font-black text-2xl md:text-3xl">
                  {getBL('forumTitle', lang)}
                </h1>
              </div>
              <p className="text-white/60 text-sm">
                {getBL('forumSubtitle', lang)}
              </p>
              <div className="flex items-center gap-4 mt-3">
                {[
                  { label: getBL('posts', lang), value: posts.length },
                  { label: getBL('members', lang), value: '128+' },
                  { label: getBL('activeToday', lang), value: '23' },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="text-white font-black text-lg">{s.value}</div>
                    <div className="text-white/50 text-[10px]">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => currentUser ? setCreateOpen(true) : setAuthOpen(true)}
              className="flex items-center gap-2 px-6 py-3 bg-[#F59E0B] text-[#1A0050] font-black rounded-xl hover:bg-amber-400 transition-colors cursor-pointer shadow-lg whitespace-nowrap self-start"
            >
              <i className="ri-edit-2-line" />
              {currentUser ? getBL('newPost', lang) : getBL('loginToPost', lang)}
            </button>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Categories */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-black text-gray-800 text-sm flex items-center gap-2">
                  <i className="ri-layout-grid-line text-[#7C3AED]" />
                  {getBL('categories', lang)}
                </h3>
              </div>
              <div className="p-2">
                {CATEGORIES.map((cat) => {
                  const count = cat.id === 'all' ? posts.length : posts.filter((p) => p.category === cat.id).length;
                  const label = CAT_LABELS[cat.id]?.[lang] ?? cat.label;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer ${
                        activeCategory === cat.id
                          ? 'text-white font-bold'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      style={activeCategory === cat.id ? { background: cat.color } : {}}
                    >
                      <div className="flex items-center gap-2">
                        <i className={`${cat.icon} text-base`} style={activeCategory === cat.id ? { color: 'white' } : { color: cat.color }} />
                        {label}
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-gradient-to-br from-[#2E1065] to-[#4C1D95] rounded-2xl p-4 text-white">
              <h3 className="font-black text-sm mb-3 flex items-center gap-2">
                <i className="ri-customer-service-2-line text-[#F59E0B]" />
                {getBL('contactAdmin', lang)}
              </h3>
              <div className="flex justify-center mb-3">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-white p-1">
                  <img
                    src="https://static.readdy.ai/image/263618122c0c25cf0393568467bcbb5e/c10996b2a623167522d37564a5ca121d.jpeg"
                    alt="WeChat QR"
                    className="w-full h-full object-contain rounded-lg"
                  />
                </div>
              </div>
              <div className="space-y-2 text-xs">
                <a href="tel:+8613810001256" className="flex items-center gap-2 text-white/70 hover:text-[#F59E0B] transition-colors cursor-pointer">
                  <i className="ri-phone-line text-[#A78BFA]" />
                  (+86) 138-1000-1256
                </a>
                <a href="mailto:13810001256@126.com" className="flex items-center gap-2 text-white/70 hover:text-[#F59E0B] transition-colors cursor-pointer">
                  <i className="ri-mail-line text-[#A78BFA]" />
                  13810001256@126.com
                </a>
              </div>
            </div>

            {/* Compliance Notice */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <h4 className="font-bold text-red-700 text-xs flex items-center gap-1.5 mb-2">
                <i className="ri-shield-check-line" />
                {getBL('complianceNotice', lang)}
              </h4>
              <p className="text-red-600 text-xs leading-relaxed">
                {getBL('complianceText', lang)}
              </p>
            </div>
          </div>

          {/* Post List */}
          <div className="lg:col-span-3 space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full" style={{ background: catInfo.color }} />
                <span className="font-black text-gray-800 text-base">{catLabel}</span>
                <span className="text-gray-400 text-sm">（{filteredPosts.length} {getBL('items', lang)}）</span>
              </div>
              <div className="flex gap-1 bg-gray-100 p-1 rounded-full">
                {(['latest', 'hot'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === s ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {s === 'latest' ? getBL('latest', lang) : getBL('hot', lang)}
                  </button>
                ))}
              </div>
            </div>

            {/* Pinned */}
            {pinnedPosts.map((post) => (
              <PostCard key={post.id} post={post} onClick={() => setSelectedPost(post)} pinned lang={lang} />
            ))}

            {/* Normal */}
            {normalPosts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <i className="ri-chat-3-line text-5xl text-gray-200 mb-3 block" />
                <p className="text-gray-400 text-sm">{getBL('noPosts', lang)}</p>
                <button
                  onClick={() => currentUser ? setCreateOpen(true) : setAuthOpen(true)}
                  className="mt-4 px-4 py-2 bg-[#7C3AED] text-white text-xs font-bold rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                >
                  {getBL('beFirst', lang)}
                </button>
              </div>
            ) : (
              normalPosts.map((post) => (
                <PostCard key={post.id} post={post} onClick={() => setSelectedPost(post)} lang={lang} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onLogin={handleLogin} />
      {selectedPost && (
        <PostDetail
          post={selectedPost}
          currentUser={currentUser}
          onClose={() => setSelectedPost(null)}
          onReply={handleReply}
          onRequireAuth={() => { setSelectedPost(null); setAuthOpen(true); }}
        />
      )}
      {currentUser && createOpen && (
        <CreatePostModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          currentUser={currentUser}
          onSubmit={handleCreatePost}
        />
      )}
      {adminOpen && <AdminPanel onClose={() => setAdminOpen(false)} />}
    </div>
  );
}

interface PostCardProps {
  post: BBSPost;
  onClick: () => void;
  pinned?: boolean;
  lang: string;
}

function PostCard({ post, onClick, pinned, lang }: PostCardProps) {
  const author = getUser(post.authorId);
  const cat = CATEGORIES.find((c) => c.id === post.category) || CATEGORIES[1];
  const catLabel = CAT_LABELS[cat.id]?.[lang] ?? cat.label;
  const hasAttachments = post.attachments.length > 0;

  return (
    <article
      onClick={onClick}
      className={`bg-white rounded-2xl border transition-all cursor-pointer group ${
        pinned
          ? 'border-[#F59E0B]/40 hover:border-[#F59E0B]/70 hover:shadow-md'
          : 'border-gray-100 hover:border-[#7C3AED]/30 hover:shadow-md'
      }`}
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-base shrink-0"
            style={{ background: `linear-gradient(135deg, #7C3AED, #A855F7)` }}>
            {author.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              {pinned && (
                <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded border border-amber-200">📌 {getBL('pinned', lang)}</span>
              )}
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                style={{ background: cat.color }}
              >
                {catLabel}
              </span>
              {post.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full">{tag}</span>
              ))}
              {hasAttachments && (
                <span className="flex items-center gap-0.5 px-2 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] text-[10px] rounded-full font-bold">
                  <i className="ri-attachment-2 text-xs" />
                  {post.attachments.length} {getBL('attachments', lang)}
                </span>
              )}
            </div>
            <h3 className="font-bold text-gray-900 text-sm group-hover:text-[#7C3AED] transition-colors line-clamp-2 mb-2">
              {post.title}
            </h3>
            <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed">
              {post.content.replace(/\*\*/g, '').slice(0, 120)}...
            </p>
            {/* Attachment Preview */}
            {hasAttachments && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {post.attachments.slice(0, 3).map((att) => {
                  const ext = att.name.split('.').pop()?.toLowerCase() || 'pdf';
                  return (
                    <div key={att.id} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200 max-w-[180px]">
                      <i className={`${FILE_ICONS[ext] || 'ri-file-line'} text-xs`} style={{ color: FILE_COLORS[ext] || '#6B7280' }} />
                      <span className="text-[10px] text-gray-500 truncate">{att.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="font-semibold text-gray-600">{author.username}</span>
            <span>{post.createdAt.slice(0, 10)}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-eye-line" />{post.views}</span>
            <span className="flex items-center gap-1"><i className="ri-heart-line" />{post.likes}</span>
            <span className="flex items-center gap-1 text-[#7C3AED] font-semibold"><i className="ri-chat-3-line" />{post.replies.length}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function BBSPage() {
  return (
    <LangProvider>
      <BBSContent />
    </LangProvider>
  );
}
